import type { Failure } from './errors'

/**
 * How the sync layer tells the rest of the app that something moved.
 *
 * This is the replacement for `onSnapshotsInSync`, which Firestore fired
 * whenever the local view settled. Nothing in Supabase corresponds to it, so
 * the queue announces its own changes and the views listen.
 */

export interface SyncEvents {
  /** A write was queued, sent, retried or quarantined. */
  'outbox-changed': void
  /** The server refused a row for good. Carries enough to show a message. */
  'permanent-failure': { rowId: string; failure: Failure }
  /** A cached table was replaced or patched. */
  'cache-changed': { table: string }
}

type Handler<K extends keyof SyncEvents> = (payload: SyncEvents[K]) => void

// Stored untyped and narrowed at the boundary. The map cannot be typed per key
// and also be written through a generic parameter, TypeScript has no way to
// know `handlers[K]` holds handlers for that same K.
const handlers = new Map<keyof SyncEvents, Set<(payload: never) => void>>()

export const syncBus = {
  on<K extends keyof SyncEvents>(event: K, fn: Handler<K>): () => void {
    let set = handlers.get(event)
    if (!set) {
      set = new Set()
      handlers.set(event, set)
    }
    set.add(fn as (payload: never) => void)
    return () => {
      set!.delete(fn as (payload: never) => void)
    }
  },

  emit<K extends keyof SyncEvents>(
    event: K,
    ...args: SyncEvents[K] extends void ? [] : [SyncEvents[K]]
  ): void {
    const set = handlers.get(event)
    if (!set) return
    const payload = args[0] as never
    // Copied before iterating: a handler that unsubscribes itself while the
    // set is being walked would otherwise skip the one after it.
    for (const fn of [...set]) {
      try {
        fn(payload)
      } catch (err) {
        // One bad listener must not stop the others, a view that throws
        // while redrawing cannot be allowed to stall the sync loop.
        console.error('[sync] dinleyici hatası', event, err)
      }
    }
  },
}
